import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { readSettings } from '../db/reader.js';
import { esperaDe, tiempoDeEspera, useEspera } from '../lib/intentos.js';
import { anotarPinIncorrecto, autorizarDueno, useSesion, verificarPinPropio, type ResultadoPin } from '../lib/sesion.js';
import { useT } from '../i18n/index.js';
import { Modal } from './Modal.js';
import { TecladoPin } from './TecladoPin.js';

/**
 * A PIN, at the moment it matters: by default the owner's, for something the signed-in user
 * cannot do alone; with `verificarPinPropio`, the user's own, before something done in their
 * name.
 *
 * Verified against the locally cached hash, so it works with no signal — which it must, because
 * the moment it is needed is the moment a customer is standing at the counter negotiating.
 * Whoever the PIN belongs to is handed back, for the caller to record; a wrong one goes in the
 * bitácora here, under whoever is signed in and typed it.
 */
export function PedirPin({
  titulo,
  motivo,
  verificar = autorizarDueno,
  donde = 'autorizacion',
  onAutorizado,
  onCancelar,
}: {
  /** Defaults to «Autorización del dueño». */
  titulo?: string;
  motivo: string;
  /** Whose PIN counts. */
  verificar?: (pin: string) => Promise<ResultadoPin>;
  /** What a wrong PIN is recorded as. Goes with `verificar`: the owner's, or the user's own. */
  donde?: 'autorizacion' | 'confirmacion';
  onAutorizado: (usuarioId: string) => void;
  onCancelar: () => void;
}) {
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [limpiar, setLimpiar] = useState(0);
  const [autorizado, setAutorizado] = useState<string | null>(null);
  const [espera, esperar] = useEspera();
  const sesion = useSesion();

  // Opened while a wait from before is still running: said at once, not after another try.
  useEffect(() => {
    const quien = donde === 'autorizacion' ? 'dueno' : sesion ? { usuarioId: sesion.usuarioId } : null;
    if (quien) esperar(esperaDe(quien));
    // Once, as the sheet opens.
  }, []);

  async function probar(pin: string) {
    setOcupado(true);
    setError(null);
    const res = await verificar(pin);
    if (res.ok && res.usuarioId) {
      // Still `ocupado` through the beat on the pad; `onExito` hands it over.
      setAutorizado(res.usuarioId);
      return;
    }
    // Turned away for the wait, the PIN was never tried: nothing for the bitácora.
    if (!res.sinProbar) await anotarPinIncorrecto(donde);
    setOcupado(false);
    setError(res.sinProbar ? null : t('pin.incorrecto'));
    setLimpiar((n) => n + 1);
    if (res.esperaMs) esperar(res.esperaMs);
  }

  return (
    <Modal titulo={titulo ?? t('pin.autorizacion')} onCerrar={onCancelar}>
      <p className="mb-5 text-sm text-slate-400">{motivo}</p>
      <div className="flex justify-center" data-guia="pin-teclado">
        <TecladoPin
          onCompleto={probar}
          ocupado={ocupado || espera > 0}
          error={espera > 0 ? t('pin.espere', { tiempo: tiempoDeEspera(espera, t) }) : error}
          limpiarToken={limpiar}
          exito={autorizado ? t('pin.aceptado') : null}
          onExito={() => autorizado && onAutorizado(autorizado)}
        />
      </div>
    </Modal>
  );
}

/**
 * Whose PIN undoing something asks for: voiding a sale or an abono, or money back for a return;
 * and voiding or correcting an expense, which only the owner does, so it is their own.
 *
 * The signed-in user's own for something from the shift still open, so that the name on it is
 * the name of whoever did it, and that till's own corte counts what it cost. Anything from
 * before — a till already closed, its money gone to general cash — takes the owner's: "the
 * customer paid cash yesterday; void it today and keep it" balances every corte there is, and
 * the owner is the one person it cannot get past. Where the owner has asked for it in
 * «Configuración» (`anularConDueno`), the shift still open takes the owner's too.
 *
 * Never when the owner is the one signed in, whose own PIN already is the owner's. The caller
 * records the owner who let it through as `autorizadoPor`, beside the actor. Until the settings,
 * and the caller its till, have been read, it is the owner's: the stricter answer is the one to
 * give unsure.
 */
export function usePinParaAnular(deOtroTurno: boolean): {
  /** The owner's PIN is being asked for, not the user's own. */
  delDueno: boolean;
  /** The owner's, and because what is being undone is from before this shift. */
  porTurno: boolean;
  titulo: string;
  verificar: (pin: string) => Promise<ResultadoPin>;
  donde: 'autorizacion' | 'confirmacion';
} {
  const t = useT();
  const sesion = useSesion();
  const config = useLiveQuery(() => readSettings(), [], null);
  const delDueno = sesion?.rol !== 'dueno' && ((config?.anularConDueno ?? true) || deOtroTurno);
  return delDueno
    ? {
        delDueno,
        porTurno: deOtroTurno,
        titulo: t('pin.autorizacion'),
        verificar: autorizarDueno,
        donde: 'autorizacion',
      }
    : {
        delDueno,
        porTurno: false,
        titulo: t('pin.confirmar'),
        verificar: verificarPinPropio,
        donde: 'confirmacion',
      };
}
